import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useContent } from "@/content";
import { media } from "@/content/media";
import { Section, Photo, BottleSilhouette } from "@/components/Layout";
import { WinnerSeal } from "@/components/Brand";
import { Reveal, SectionHead, GoldLine, Eyebrow, MaskedTitle } from "@/components/Motion";

export const StatusBanner = ({ children, testid }) => (
  <div className="inline-flex items-center gap-4 border border-copper/60 px-6 py-3 text-[0.75rem] uppercase tracking-[0.3em] text-copper" data-testid={testid}>
    <span className="h-1.5 w-1.5 rounded-full bg-copper" aria-hidden="true" />{children}
  </div>
);

export const ProductCard = ({ product, index }) => {
  return (
    <Reveal delay={(index % 4) * 0.08} className="group" data-testid={`product-card-${index + 1}`}>
      <div className="relative aspect-[3/4] overflow-hidden rounded-[5px] border border-white/20 bg-white/[0.06]">
        {product ? (
          <img src={product.image} alt={product.name} className="photo-treat h-full w-full object-cover transition-transform duration-1000 group-hover:scale-105" loading="lazy" />
        ) : (
          <>
            <div className="photo-spot absolute inset-0" aria-hidden="true" />
            <BottleSilhouette className="absolute left-1/2 top-1/2 h-2/3 -translate-x-1/2 -translate-y-1/2 text-copper/50 transition-transform duration-1000 group-hover:scale-105" />
          </>
        )}
      </div>
      <p className="mt-5 font-serif text-xl leading-tight">{product ? product.name : ""}</p>
      <p className="mt-1 text-[0.75rem] uppercase tracking-[0.25em] text-copper">{product ? product.brand : ""}</p>
      <p className="mt-1 text-[0.75rem] uppercase tracking-[0.2em] text-white/85">{product ? product.category : ""}</p>
    </Reveal>
  );
};

export const ProductGrid = ({ products, slots = 8, testid }) => (
  <div className="grid grid-cols-2 gap-x-5 gap-y-12 md:grid-cols-4 md:gap-x-8" data-testid={testid}>
    {Array.from({ length: Math.max(slots, products.length) }, (_, i) => <ProductCard key={i} index={i} product={products[i]} />)}
  </div>
);

const BenefitList = ({ items, testid, light = false, mosaic = false }) => (
  <ul className={`grid gap-x-12 md:grid-cols-2 ${mosaic ? "lg:grid-cols-4 lg:gap-x-10" : ""}`} data-testid={testid}>
    {items.map((it, i) => (
      <Reveal as="li" key={it.title} delay={(i % 2) * 0.08} className={`border-t border-copper/25 py-7 ${mosaic && (i < 2 || i >= 6) ? "lg:col-span-2" : ""}`}>
        <div className="flex items-baseline gap-4">
          <span className="font-serif text-lg text-copper">{String(i + 1).padStart(2, "0")}</span>
          <h3 className="font-sans text-[0.78rem] font-medium uppercase leading-relaxed tracking-[0.2em]">{it.title}</h3>
        </div>
        <p className={`mt-3 ps-9 text-[0.97rem] leading-relaxed ${light ? "text-brown/90" : "text-white/85"}`}>{it.text}</p>
      </Reveal>
    ))}
  </ul>
);

export const FinalistsSection = ({ full = false }) => {
  const f = useContent().finalists;
  const ui = useContent().ui;
  return (
    <Section id={f.id} tone="aubergine" className="py-20 md:py-28">
      <div className="grid items-end gap-12 lg:grid-cols-[1fr_0.8fr]">
        <div>
          <SectionHead eyebrow={f.eyebrow} title={f.title} intro={f.intro} testid="finalists-title" />
          <Reveal className="mt-8"><StatusBanner testid="finalists-status">{f.status}</StatusBanner></Reveal>
        </div>
        <Reveal><Photo src={media.finalists} alt={f.imageAlt} frame="cutr" className="aspect-[16/10]" /></Reveal>
      </div>
      <Reveal className="mt-20"><p className="mb-6 text-[0.75rem] uppercase tracking-[0.3em] text-copper">{f.listTitle}</p></Reveal>
      <BenefitList items={f.items} testid="finalists-benefits" />
      {!full && (
        <Reveal className="mt-12">
          <Link to="/finalistes" data-testid="finalists-see-page" className="link-line inline-flex items-center gap-2 text-[0.75rem] uppercase tracking-[0.3em] text-copper">{ui.seeFinalists} <ArrowUpRight size={14} strokeWidth={1.2} /></Link>
        </Reveal>
      )}
    </Section>
  );
};

export const WinnersSection = ({ full = false }) => {
  const w = useContent().winners;
  const ui = useContent().ui;
  return (
    <Section id={w.id} tone="ceremony" className="py-20 md:py-28">
      <div className="grid items-center gap-12 lg:grid-cols-[1fr_auto] lg:gap-24">
        <div>
          <SectionHead eyebrow={w.eyebrow} title={w.title} intro={w.intro} tone="white" testid="winners-title" />
          <Reveal className="mt-8"><StatusBanner testid="winners-status">{w.status}</StatusBanner></Reveal>
        </div>
        <Reveal className="mx-auto"><WinnerSeal size={220} /></Reveal>
      </div>
      <Reveal className="mt-20"><p className="mb-6 text-[0.75rem] uppercase tracking-[0.3em] text-copper">{w.listTitle}</p></Reveal>
      <BenefitList items={w.items} testid="winners-benefits" mosaic />
      <Reveal className="mt-10"><p className="max-w-3xl text-sm leading-relaxed text-brown/90">{w.nb}</p></Reveal>
      {!full && (
        <Reveal className="mt-12">
          <Link to="/gagnants" data-testid="winners-see-page" className="link-line inline-flex items-center gap-2 text-[0.75rem] uppercase tracking-[0.3em] text-copper">{ui.seeWinners} <ArrowUpRight size={14} strokeWidth={1.2} /></Link>
        </Reveal>
      )}
    </Section>
  );
};

export { GoldLine, Eyebrow, MaskedTitle };
